import type { RealtimeConnectionOptions } from "./realtime-connection";

import { createAgentCoordinatorName } from "replo-utils/lib/agentCoordinatorName";
import { AGENT_BASE_PATH } from "replo-utils/lib/agentRoutes";
import * as z from "zod";

import { getAgentRoutingParams, parseAgentRoutingFields } from "./host-params";
import { RealtimeConnection } from "./realtime-connection";

const jwtPayloadSchema = z.object({ sub: z.string() });

interface PreconnectState {
  startedAtMs: number;
  projectId: string;
}

// (Andrew, 2026-07-29): Unadopted connections die here so an SPA nav away can't leak a live DO connection.
const UNADOPTED_CONNECTION_TTL_MS = 60_000;

let preconnectState: PreconnectState | null = null;

let slot: {
  connection: RealtimeConnection;
  agentCoordinatorName: string;
  agentCoordinatorBaseUrl: string;
} | null = null;

// NOTE (Andrew, 2026-07-31): For telemetry joins; live from dial start until the dial fails or is reaped unadopted.
export function getPreconnectState(): PreconnectState | null {
  return preconnectState;
}

// (Andrew, 2026-07-29): Both the early dial and adoption derive the DO name here, so the two sides of the match can never drift.
function resolveAgentCoordinatorName({
  projectId,
  actorId,
  routing,
}: {
  projectId: string;
  actorId: string;
  routing: RealtimeConnectionOptions["routing"];
}): string {
  const fields = routing ?? parseAgentRoutingFields(getAgentRoutingParams());
  return createAgentCoordinatorName({
    projectId,
    actorId,
    devAlias: fields.devAlias,
    initialSnapshotRef: fields.initialSnapshotRef,
  });
}

// NOTE (Andrew, 2026-07-29): Take-once adoption: attaches the consumer's handlers and hands over the connection only when it targets the exact DO the caller is about to dial; a mismatch is left for the TTL to reap.
export function adoptPreconnectedAgentConnection({
  projectId,
  actorId,
  agentCoordinatorBaseUrl,
  options,
}: {
  projectId: string;
  actorId: string;
  agentCoordinatorBaseUrl: string;
  options: RealtimeConnectionOptions | undefined;
}): RealtimeConnection | null {
  if (
    !slot ||
    slot.agentCoordinatorName !==
      resolveAgentCoordinatorName({
        projectId,
        actorId,
        routing: options?.routing,
      }) ||
    slot.agentCoordinatorBaseUrl !== agentCoordinatorBaseUrl
  ) {
    return null;
  }
  const connection = slot.connection;
  slot = null;
  connection.attach(options ?? {});
  return connection;
}

// NOTE (Andrew, 2026-07-29): Unverified read by design — the DO re-verifies; `sub` only derives the same DO name the real dial will build.
function decodeJwtSub(token: string): string | null {
  const payloadSegment = token.split(".")[1];
  if (!payloadSegment) {
    return null;
  }
  try {
    const json = atob(payloadSegment.replace(/-/g, "+").replace(/_/g, "/"));
    const parsed = jwtPayloadSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data.sub : null;
  } catch {
    return null;
  }
}

// NOTE (Andrew, 2026-07-29): Navigation-time hint with `<link rel="preconnect">` contract — advisory, idempotent, fire-and-forget. Starts the real agent connection (one-shot, so a logged-out user fails silently once, never a retry loop); `connectRealtime` adopts it when React mounts, and every failure leaves the normal dial path untouched.
export function preconnectAgent({
  projectId,
  agentCoordinatorBaseUrl,
  getToken,
  routing,
}: {
  projectId: string;
  agentCoordinatorBaseUrl: string;
  getToken: (options?: { forceRefreshToken?: boolean }) => Promise<string>;
  // Must be what the consumer will pass to connectRealtime: the DO name is derived from it, and a mismatch means no adoption.
  routing?: RealtimeConnectionOptions["routing"];
}) {
  const startedAtMs = Date.now();
  void (async () => {
    const token = await getToken();
    const actorId = decodeJwtSub(token);
    if (!actorId) {
      return;
    }
    const agentCoordinatorName = resolveAgentCoordinatorName({
      projectId,
      actorId,
      routing,
    });
    const websocketUrl = `${agentCoordinatorBaseUrl.replace(/^http/, "ws")}${AGENT_BASE_PATH}/${agentCoordinatorName}`;
    const connection = new RealtimeConnection({
      autoReconnect: false,
      bufferMessagesUntilAttach: true,
      resolveAuthToken: async ({ forceRefreshToken }) => ({
        token: await getToken({ forceRefreshToken }),
        projectId,
        userId: actorId,
      }),
      buildWebsocketUrl: () => websocketUrl,
      onDialFailed: () => {
        preconnectState = null;
        if (slot?.connection === connection) {
          slot = null;
        }
        // Nobody will adopt it, so shut it down — otherwise its heartbeat interval outlives the page.
        connection.close();
      },
    });
    slot = { connection, agentCoordinatorName, agentCoordinatorBaseUrl };
    preconnectState = { startedAtMs, projectId };
    setTimeout(() => {
      if (slot?.connection === connection) {
        slot = null;
        preconnectState = null;
        connection.close();
      }
    }, UNADOPTED_CONNECTION_TTL_MS);
  })().catch(() => {
    // (Andrew, 2026-07-29): Advisory hint: failure means "no head start", never an error.
  });
}
